import React, { ComponentProps, useContext } from 'react'
import { useDrag } from 'react-dnd'

import { IconButton } from '@/shared/components'
import { Icon } from '@/shared/components/Icon/Icon'
import { MosaicDragType } from '@/shared/components/Mosaic'
import { ToolWidgetType } from '@/shared/components/Mosaic/models/widgetType'
import { MOSAIC_LAYOUT_ID } from '@/shared/config/consts'
import { pxToRem } from '@/shared/styles/themes'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { ButtonBase, styled, Tooltip, TooltipProps } from '@mui/material'

import { useDragItemMonitor } from './lib/useDragItemMonitor'
import { WidgetToolbarContext } from './WidgetToolbarContext'

export type WidgetButtonProps = ComponentProps<typeof ButtonBase> & {
  icon: IconDefinition
  tooltipTitle?: string
  index: number
  widgetType: ToolWidgetType
  slotProps?: {
    tooltip?: Omit<TooltipProps, 'title' | 'children'>
  }
}

type ExpectedDropResult = {
  isNewWindow?: boolean
}

export const WidgetButton: React.FC<WidgetButtonProps> = ({ icon, tooltipTitle, index, widgetType, slotProps, ...props }) => {
  const { isWidgetSettingsOpen, moveWidgetToWindow, hideWidget } = useContext(WidgetToolbarContext)
  const isWidgetButtonDragging = useDragItemMonitor().isDragging

  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: MosaicDragType.WIDGET_BUTTON,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    item: {
      index: index,
      type: MosaicDragType.WIDGET_BUTTON,
      mosaicId: MOSAIC_LAYOUT_ID,
      widgetType: widgetType,
    },
    end: (_, monitor) => {
      const dropResult = (monitor.getDropResult() || {}) as ExpectedDropResult

      // Remove the widget button if the drop target is a mosaic window
      if (monitor.didDrop() && dropResult?.isNewWindow) {
        moveWidgetToWindow(index)
      }
    },
  }), [index, widgetType, moveWidgetToWindow])

  return (
    <div
      ref={dragRef}
      style={{
        visibility: isDragging ? 'hidden' : 'visible',
        width: isDragging ? 0 : 'unset',
        marginRight: isDragging ? 0 : pxToRem(8),
        borderRadius: pxToRem(8),
        transform: 'translate(0, 0)',
        position: 'relative',
      }}>
      <Tooltip title={isWidgetButtonDragging ? '' : tooltipTitle} arrow placement="bottom" {...slotProps?.tooltip}>
        <StyledWidgetButton
          disableRipple
          {...props}
        >
          <Icon icon={icon} />
        </StyledWidgetButton>
      </Tooltip>
      {isWidgetSettingsOpen && <StyledRemoveButton onClick={() => { hideWidget(widgetType, index) }}>
        <Icon sx={{ fontSize: '12px' }} icon={faClose} />
      </StyledRemoveButton>}
    </div>
  )
}

const StyledWidgetButton = styled(ButtonBase)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${pxToRem(48)};
  height: ${pxToRem(32)};
  border-radius: ${pxToRem(8)};
  background-color: ${({ theme }) => theme.palette.custom.widgetButton.backgroundColor};
  color: ${({ theme }) => theme.palette.text.secondary};
  box-shadow: ${({ theme }) => theme.palette.custom.widgetButton.boxShadow};
  border: ${({ theme }) => theme.palette.custom.widgetButton.border};

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.custom.widgetButton.hoverBackgroundColor};
  }
`

const StyledRemoveButton = styled(IconButton)`
  position: absolute;
  top: -${pxToRem(8)};
  right: -${pxToRem(8)};
  margin: 0;
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
  background-color: ${({ theme }) => theme.palette.action.hover};
`